const MS_PER_DAY = 1000 * 60 * 60 * 24;
const THREE_MONTHS_DAYS = 92;

function isoWeekToDate(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1 + (week - 1) * 7);
  return new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate());
}

export function parseTrendLabel(label, granularity = 'day') {
  const raw = String(label || '').trim();
  if (!raw) return null;

  const dayMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dayMatch) {
    return new Date(+dayMatch[1], +dayMatch[2] - 1, +dayMatch[3]);
  }

  const isoWeekMatch = raw.match(/^(\d{4})-W(\d{1,2})$/i);
  if (isoWeekMatch) {
    return isoWeekToDate(+isoWeekMatch[1], +isoWeekMatch[2]);
  }

  const twoPart = raw.match(/^(\d{4})-(\d{1,2})$/);
  if (twoPart) {
    const year = +twoPart[1];
    const num = +twoPart[2];
    if (granularity === 'week' || num > 12) {
      return isoWeekToDate(year, num);
    }
    if (granularity === 'month') {
      return new Date(year, num - 1, 1);
    }
    return new Date(year, num - 1, 1);
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
}

export function compareTrendLabels(a, b, granularity = 'day') {
  const dateA = parseTrendLabel(a, granularity);
  const dateB = parseTrendLabel(b, granularity);
  if (dateA && dateB) return dateA.getTime() - dateB.getTime();
  return String(a).localeCompare(String(b));
}

export function resolveTrendGranularity({ startDate, endDate } = {}) {
  const start = String(startDate || '').trim();
  const end = String(endDate || '').trim();

  if (!start || !end) {
    return 'day';
  }

  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    return 'month';
  }

  const diffDays = Math.floor((endMs - startMs) / MS_PER_DAY) + 1;

  if (diffDays <= THREE_MONTHS_DAYS) return 'day';
  return 'month';
}

export function inferTrendGranularityFromSeries(series = {}, fallback = 'day') {
  const labels = [];
  ['created', 'sent', 'completed', 'declined'].forEach((key) => {
    const points = Array.isArray(series[key]) ? series[key] : Array.isArray(series[key]?.data) ? series[key].data : [];
    points.forEach((point) => {
      const label = point?.date ?? point?.label ?? point?.period;
      if (label) labels.push(String(label));
    });
  });

  if (!labels.length) return fallback;

  const parseAny = (label) =>
    parseTrendLabel(label, 'day') || parseTrendLabel(label, 'week') || parseTrendLabel(label, 'month');

  labels.sort((a, b) => {
    const dateA = parseAny(a);
    const dateB = parseAny(b);
    if (dateA && dateB) return dateA.getTime() - dateB.getTime();
    return String(a).localeCompare(String(b));
  });
  const first = parseAny(labels[0]);
  const last = parseAny(labels[labels.length - 1]);

  if (!first || !last) return fallback;

  const spanDays = Math.floor((last.getTime() - first.getTime()) / MS_PER_DAY) + 1;
  return spanDays > THREE_MONTHS_DAYS ? 'month' : 'day';
}

export function formatTrendLabel(label, granularity = 'day') {
  if (!label) return '';
  const raw = String(label).trim();

  const dayMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dayMatch) {
    const date = new Date(+dayMatch[1], +dayMatch[2] - 1, +dayMatch[3]);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const isoWeekMatch = raw.match(/^(\d{4})-W(\d{1,2})$/i);
  const twoPart = raw.match(/^(\d{4})-(\d{1,2})$/);
  const weekMatch = isoWeekMatch
    || (twoPart && (granularity === 'week' || +twoPart[2] > 12) ? twoPart : null);

  if (weekMatch) {
    const weekStart = isoWeekToDate(+weekMatch[1], +weekMatch[2]);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startLabel = weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const endLabel = weekEnd.toLocaleDateString(undefined, {
      month: weekEnd.getMonth() === weekStart.getMonth() ? undefined : 'short',
      day: 'numeric',
    });
    return `${startLabel} – ${endLabel}`;
  }

  const monthMatch = twoPart && +twoPart[2] <= 12 ? twoPart : raw.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch && (granularity === 'month' || (+monthMatch[2] <= 12 && !weekMatch))) {
    const date = new Date(+monthMatch[1], +monthMatch[2] - 1, 1);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }

  const parsed = parseTrendLabel(raw, granularity);
  if (!parsed) return raw;

  if (granularity === 'month') {
    return parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }
  if (granularity === 'week') {
    const weekEnd = new Date(parsed);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startLabel = parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const endLabel = weekEnd.toLocaleDateString(undefined, {
      month: weekEnd.getMonth() === parsed.getMonth() ? undefined : 'short',
      day: 'numeric',
    });
    return `${startLabel} – ${endLabel}`;
  }

  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatTrendAxisLabel(label, granularity = 'day') {
  const parsed = parseTrendLabel(label, granularity);
  if (!parsed) return formatTrendLabel(label, granularity);

  if (granularity === 'month') {
    return parsed.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function trendGranularityLabel(granularity) {
  if (granularity === 'week') return 'Weekly';
  if (granularity === 'month') return 'Monthly';
  return 'Daily';
}
